import { ApiError } from "@/api/client";
import type {
  AdoptionPin,
  AssetVersionRef,
  LibraryAsset,
  LibraryAssetVersionDetail,
  QuestionnaireContent,
} from "@/api/library";
import {
  isServerAnsweredFailure,
  isTerminalQueryError,
} from "@/api/queryClient";

import { isQuestionnaireContent } from "./questionnaire";

/**
 * What a Project's pinned questionnaire IS, as arithmetic rather than as a
 * screen (#369).
 *
 * The gap this closes. A clinician opening `/projects/:id/adoptions` saw, for a
 * pinned questionnaire, a name, a version label and a type badge — nothing
 * about the instrument itself. To learn how many items it administers, whether
 * it is scored, or whether its validity screen fires, they had to leave the
 * Project and open the library asset, where the surface shows whichever version
 * they navigate to. That is not necessarily the version this Project pins, and
 * the surface that would reveal the difference was the one that did not show
 * it.
 *
 * TWO RULES SHAPE EVERY FUNCTION BELOW.
 *
 * 1. **The pinned version, never the head.** The detail is read at
 *    `queryKeys.assetVersion(assetId, pin.version)` — the version the PIN
 *    names. `LibraryAsset.latestVersion` is a fact about the library and is
 *    already rendered as such ("v4 available in the library"); it never sources
 *    a single word of what the project runs.
 * 2. **An unread detail is unread, not absent.** "This questionnaire has no
 *    severity bands" and "this questionnaire's version could not be read" are
 *    different sentences with different owners, and a surface that renders them
 *    identically tells a clinician the instrument is simpler than it is. So the
 *    union below has a distinct arm for each, and `severityBandSentence` says
 *    "No severity bands" only for content it actually holds.
 * 3. **Nor is one unread the same as another.** The governing `## Clarifications`
 *    requires pending, paused and error to stay distinguished "as the
 *    surrounding surface already distinguishes them", and that surface is
 *    specific about what the distinctions are: `QueryPendingState` separates a
 *    pause with no response ever received — the connection — from a pause over
 *    a failure the Editor Service answered (#215, refined by #327), and
 *    `QueryErrorState` separates an expired sign-in, a denial and an
 *    unimplemented endpoint from a transient failure, because each has a
 *    different recovery and offering the wrong one is its own defect (#4, #44).
 *    This module reproduces exactly those distinctions and no more, from the
 *    same predicates those two components use, so the row and the screens
 *    around it cannot drift into disagreeing about what a status means.
 *
 * WHAT THIS MODULE MAY NOT DO. It may not decide whether something is locked —
 * `lib/adoptions.ts` states that rule for the whole surface. Questionnaires
 * carry no guard floor (they are a born-editor type with no pack counterpart;
 * the floor is `safety_track`, per the service's own `adoption_serializer`), so
 * `AssetCapabilities.lockedFloor` is false on every one of them and
 * `adoptionLock` already returns `null`. Suppressing a padlock HERE, on the
 * asset type, would be the browser overruling a server report rather than
 * reading it — and it would hide a real floor the day the service declared one.
 * The rule is demonstrated by tests, not enforced by a second opinion.
 */

/**
 * Why a pinned version's content is not on screen.
 *
 * Named separately rather than collapsed into one "unavailable", for the reason
 * `SectionAdoptionBlock.UNAVAILABLE_DETAIL` names its four: a surface that
 * renders a transient outage and a refusal identically is telling a clinician
 * the same thing about two different problems with two different owners — and
 * only one of them is worth a retry.
 */
export type QuestionnaireDetailUnreadReason =
  /**
   * The retry is held and no response ever arrived — the connection, not the
   * service.
   *
   * The only arm permitted to borrow the shell's offline vocabulary, for the
   * reason `isServerAnsweredFailure` exists: a 503 the Editor Service really
   * sent, parked because the tab is in the background, is not a connectivity
   * problem and must never be described as one (#327).
   */
  | "offline"
  /**
   * The read failed and a retry can still clear it — a settled transient
   * failure, or a pause over a failure the service already answered.
   *
   * Those two share one arm deliberately, and it is the one place this module
   * folds two conditions together. `QueryPendingState` is explicit about why:
   * an answered pause is the SAME failure the surface's own error branch
   * renders once the retries are spent, so it is given that branch's words
   * verbatim — "two headings for one failure would read as two different
   * problems". What the pause changes is not the sentence but the classifying:
   * it must not be mistaken for `offline`, which is what carrying
   * `pausedAssetIds` prevents.
   */
  | "read_failed"
  /** 401 — the sign-in itself is gone. Only a new sign-in helps (#44). */
  | "session_expired"
  /** 403 — a denial. Retrying will not grant access. */
  | "read_denied"
  /** 501 — the endpoint is not built yet, so there is nothing to retry (#13). */
  | "not_implemented"
  /** Any other terminal answer — 404, 422, a terminal 409. */
  | "read_refused"
  /** It answered, with content that is not a questionnaire's. */
  | "content_kind_mismatch";

/**
 * The one control this block may offer, named rather than implied.
 *
 * A boolean said only whether to render a re-read, which is why a 401 arrived
 * at the row wearing no control at all: the recovery for an expired session
 * exists in this shell and is a sign-in, not a refetch. Naming the recovery
 * keeps the component free of the decision, exactly as the lock beside it is a
 * prop rather than a judgement the row makes.
 */
export type QuestionnaireDetailRecovery =
  /** Read the pinned version again. */
  | "reread"
  /** Sign in again, then the read can be retried. */
  | "sign_in"
  /** None can work, so none is offered (#4). */
  | "none";

/** What the adoptions surface knows about one pinned questionnaire version. */
export type QuestionnaireAdoptionDetail =
  | { readonly state: "read"; readonly content: QuestionnaireContent }
  | { readonly state: "reading" }
  | {
      readonly state: "unread";
      readonly reason: QuestionnaireDetailUnreadReason;
      /** The service's own machine-readable code, when it answered with one. */
      readonly code: string | null;
      /** The control this state may offer, if any. */
      readonly recovery: QuestionnaireDetailRecovery;
    };

/**
 * The version-detail reads, as `useAssetVersionQueries` classified them.
 *
 * Structural rather than an import of the hook's return type, so this module
 * stays pure and testable from plain values — the discipline `lib/adoptions.ts`
 * and `lib/sectionAdoption.ts` both keep.
 */
export type VersionDetailReads = {
  readonly byAssetId: ReadonlyMap<string, LibraryAssetVersionDetail>;
  readonly pendingAssetIds: ReadonlySet<string>;
  readonly failedAssetIds: ReadonlySet<string>;
  /** The failed reads whose retry is being held rather than settled. */
  readonly pausedAssetIds: ReadonlySet<string>;
  /** `error` once settled, `failureReason` while the retry is held. */
  readonly failureByAssetId: ReadonlyMap<string, unknown>;
};

/**
 * Which unread this failure is, and what — if anything — can clear it.
 *
 * ## Terminal answers first, before the pause is consulted
 *
 * `isServerAnsweredFailure`'s own documentation records that a paused query's
 * failure is never terminal: `retry` returns false for `isTerminalQueryError`
 * and the retryer rejects on `!shouldRetry` before it ever sleeps, so a 401,
 * 403, 404, 501 or terminal 409 settles as an error and never reaches a paused
 * surface. This ordering does not depend on that holding. A status the service
 * ANSWERED is a fact about the service whether or not a retry happens to be
 * parked on top of it, and describing one as a connectivity problem is the
 * error #327 was filed for — so the answered statuses are read first and the
 * pause decides only what is left.
 *
 * ## Why these arms and not others
 *
 * They are `QueryErrorState`'s branches, one for one: 501 before 401 before
 * 403, then everything else terminal, then the transient tail. The three named
 * statuses are the ones with a recovery of their own in this shell; 404, 422
 * and a terminal 409 reach that component's plain error frame with no retry
 * attached, which is what `read_refused` renders here.
 */
function unreadFromFailure(
  failure: unknown,
  paused: boolean,
): {
  readonly reason: QuestionnaireDetailUnreadReason;
  readonly recovery: QuestionnaireDetailRecovery;
} {
  if (failure instanceof ApiError) {
    if (failure.isNotImplemented) {
      return { reason: "not_implemented", recovery: "none" };
    }
    // Unauthenticated mid-session is expiry, not denial (#44): silent renewal
    // ran out of road, and a re-read would be refused for as long as that is
    // true. The control that CAN change the answer is a new interactive
    // sign-in, which is why this arm carries one and the denial below does not.
    if (failure.isUnauthenticated) {
      return { reason: "session_expired", recovery: "sign_in" };
    }
    if (failure.isForbidden) {
      return { reason: "read_denied", recovery: "none" };
    }
    if (isTerminalQueryError(failure)) {
      return { reason: "read_refused", recovery: "none" };
    }
  }

  if (paused && !isServerAnsweredFailure(failure)) {
    return { reason: "offline", recovery: "reread" };
  }

  return { reason: "read_failed", recovery: "reread" };
}

/**
 * The exact versions whose content this surface reads: the questionnaire pins,
 * and nothing else.
 *
 * Bounded to one asset type deliberately. Every other row on the surface
 * behaves exactly as it did, and the number of extra requests is the number of
 * questionnaires this project pins rather than the size of its pin set.
 *
 * The family is the PIN's own (#615): the service joins `assetType` onto each
 * stored pin, so which pins are questionnaires is known from the pin set alone
 * and a catalog read that failed or omitted the asset does not turn a
 * questionnaire into "type unknown". Nothing is guessed — a pin whose stored
 * family is not `questionnaire` is excluded exactly as before.
 */
export function questionnaireVersionRefs(
  pins: readonly AdoptionPin[],
): readonly AssetVersionRef[] {
  return pins
    .filter((pin) => pin.assetType === "questionnaire")
    .map((pin) => ({ assetId: pin.assetId, version: pin.version }));
}

/**
 * What one adoption row should say about its pinned instrument, or `null` when
 * the row is not a questionnaire's and nothing extra is said at all.
 *
 * `null` is the whole of this ticket's scope fence: an alert matrix, a pathway
 * or a pin the catalog could not name renders exactly as it did before.
 */
export function questionnaireAdoptionDetail(
  pin: AdoptionPin,
  asset: LibraryAsset | undefined,
  reads: VersionDetailReads,
): QuestionnaireAdoptionDetail | null {
  if (asset === undefined || asset.assetType !== "questionnaire") {
    return null;
  }

  const detail = reads.byAssetId.get(pin.assetId);
  if (detail !== undefined) {
    // The cache entry is keyed by asset AND version, so what came back is the
    // content of the version this pin names — the head's content has no route
    // into this branch.
    return isQuestionnaireContent(detail.content)
      ? { state: "read", content: detail.content }
      : {
          // Two server reads disagreeing about what this asset is. Rendering
          // the row as though it simply had no detail would hide the
          // disagreement; it is reported instead, and no retry is offered
          // because a re-read returns the same answer.
          state: "unread",
          reason: "content_kind_mismatch",
          code: null,
          recovery: "none",
        };
  }

  if (reads.failedAssetIds.has(pin.assetId)) {
    const failure = reads.failureByAssetId.get(pin.assetId);
    return {
      state: "unread",
      ...unreadFromFailure(failure, reads.pausedAssetIds.has(pin.assetId)),
      // The code, never the message — `ProjectionNotice` draws the same line on
      // the same surface: a code is a stable fact a clinician can quote to
      // whoever owns the service, and a prose message is not. A pause carries
      // one too now, where the service answered before the retry was held.
      code: failure instanceof ApiError ? failure.code : null,
    };
  }

  // Anything not yet answered is still being read. Unreachable for a caller
  // that derives its refs from these same pins — `questionnaireVersionRefs`
  // issues one read per questionnaire pin, and `useQueries` reports it pending
  // in the very render that adds it — and stated rather than left to fall
  // through to a branch that would claim the instrument has no detail.
  return { state: "reading" };
}
